import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Loader2, Save, Phone, FileText, Receipt, CreditCard, ShieldCheck, Users, Briefcase } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  {
    title: "Customer Information",
    icon: Users,
    keys: ["view_customer_phone", "add_customer_email", "edit_customer_email"],
  },
  {
    title: "Estimates",
    icon: FileText,
    keys: ["create_estimates", "edit_estimates", "send_estimates", "send_estimates_for_signature"],
  },
  {
    title: "Invoices",
    icon: Receipt,
    keys: ["create_invoices", "edit_invoices", "send_invoices", "send_invoices_for_signature"],
  },
  {
    title: "Payments & Signatures",
    icon: CreditCard,
    keys: ["collect_payments", "collect_deposits", "tap_to_pay", "view_payment_history", "view_customer_signature"],
  },
  {
    title: "Jobs",
    icon: Briefcase,
    keys: ["set_job_status"],
  },
];

const DESCRIPTIONS = {
  view_customer_phone: "See the customer's real phone number (when the global setting allows it)",
  add_customer_email: "Add an email when the customer has none on file",
  edit_customer_email: "Change an existing customer email",
  send_estimates_for_signature: "Send estimates to customers for electronic signature (SMS/Email)",
  send_invoices_for_signature: "Send invoices to customers for electronic signature (SMS/Email)",
  collect_deposits: "Record deposits / partial payments in the field",
  tap_to_pay: "Accept contactless cards / Apple Pay / Google Pay on their phone in the mobile app (business must have Tap to Pay enabled)",
  view_customer_signature: "View the customer's signature image and signer details",
  set_job_status: "Change a job's status to Estimate, Deposit Collected, or Cancelled from the Tech Portal",
};

// Shared permission toggle grid — used for account defaults AND per-tech overrides
export const PermissionToggleGrid = ({ values, labels, onChange, testPrefix = "perm" }) => (
  <div className="space-y-4">
    {GROUPS.map((group) => (
      <div key={group.title}>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <group.icon className="w-3.5 h-3.5" /> {group.title}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {group.keys.map((key) => (
            <label
              key={key}
              className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-theme/40 bg-muted/20 cursor-pointer"
              data-testid={`${testPrefix}-${key}`}
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground">{labels?.[key] || key.replace(/_/g, " ")}</p>
                {DESCRIPTIONS[key] && (
                  <p className="text-[10px] text-muted-foreground leading-snug">{DESCRIPTIONS[key]}</p>
                )}
              </div>
              <Switch
                checked={values?.[key] !== false}
                onCheckedChange={(v) => onChange(key, v)}
                data-testid={`${testPrefix}-switch-${key}`}
              />
            </label>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const TechPermissionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPhone, setShowPhone] = useState(true);
  const [defaults, setDefaults] = useState({});
  const [labels, setLabels] = useState({});

  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  useEffect(() => {
    axios.get(`${API}/api/settings/technician-permissions`, authConfig())
      .then((res) => {
        setShowPhone(res.data.show_customer_phone !== false);
        setDefaults(res.data.default_permissions || {});
        setLabels(res.data.permission_labels || {});
      })
      .catch(() => toast.error("Failed to load technician permission settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/technician-permissions`, {
        show_customer_phone: showPhone,
        default_permissions: defaults,
      }, authConfig());
      toast.success("Technician permission settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="tech-permissions-tab">
      {/* Global phone visibility */}
      <Card className="glass-card border-theme" data-testid="phone-visibility-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" /> Show Customer Phone Number to Technicians
          </CardTitle>
          <CardDescription className="text-xs">
            When disabled, technicians never see real customer phone numbers anywhere in the Tech Portal —
            job details, documents, notifications, or clickable phone links. Technicians with an assigned
            Call Masking number can still call customers through the masking system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-theme/40 bg-muted/20 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-foreground">{showPhone ? "Enabled" : "Disabled"}</p>
              <p className="text-[11px] text-muted-foreground">
                {showPhone
                  ? "Technicians can see customer phone numbers (per their individual permission)"
                  : "Customer phone numbers are hidden from ALL technicians"}
              </p>
            </div>
            <Switch checked={showPhone} onCheckedChange={setShowPhone} data-testid="show-customer-phone-toggle" />
          </label>
          {!showPhone && (
            <p className="text-[11px] text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 mt-3" data-testid="phone-hidden-masking-note">
              Tip: enable Call Masking (Settings → Call Masking) and assign masking numbers so technicians
              can still call customers without seeing their real number.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Default permissions */}
      <Card className="glass-card border-theme" data-testid="default-permissions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> Default Technician Permissions
          </CardTitle>
          <CardDescription className="text-xs">
            Applied to every technician unless you set custom permissions for them (Edit Technician → Permissions tab).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PermissionToggleGrid
            values={defaults}
            labels={labels}
            onChange={(key, v) => setDefaults((d) => ({ ...d, [key]: v }))}
            testPrefix="default-perm"
          />
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-permissions-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Permission Settings
      </Button>
    </div>
  );
};

export default TechPermissionsTab;
